import {
	ref,
	computed,
	watch,
	onMounted,
	nextTick,
	getCurrentInstance,
} from "vue";
import { useTheme } from "vuetify";
import { availableThemes, getThemeByName, type OrixaTheme } from "@/themes";

// Função fallback para quando não estamos em contexto de componente
function createFallbackTheme() {
	const currentThemeName = ref("ori");
	const currentTheme = computed(() => availableThemes[0]);
	const themes = computed(() => availableThemes);
	const isDark = ref(false);

	return {
		currentThemeName: computed(() => currentThemeName.value),
		currentTheme,
		themes,
		changeTheme: () => {},
		toggleDark: () => {},
		isDark,
		applyTheme: () => {},
	};
}

export function useOrixaTheme() {
	// Verificar se estamos em um contexto válido do Vue
	if (!getCurrentInstance()) {
		console.warn("useOrixaTheme should be called within a component setup");
		return createFallbackTheme();
	}

	const vuetifyTheme = useTheme();

	// Tema atual (nome)
	const currentThemeName = ref<string>(
		localStorage.getItem("orixaTheme") || "ori"
	);

	// Tema atual (objeto)
	const currentTheme = computed(() => {
		return getThemeByName(currentThemeName.value) || availableThemes[0];
	});

	// Lista de temas disponíveis
	const themes = computed(() => availableThemes);

	// Aplicar tema no Vuetify
	const applyTheme = (themeName: string) => {
		nextTick(() => {
			// Verificar se o Vuetify theme está disponível
			if (!vuetifyTheme?.global?.name) {
				console.warn("Vuetify theme not available");
				return;
			}

			const isDarkMode = vuetifyTheme.global.name.value.includes("-dark");
			const newThemeName = `${themeName}-${isDarkMode ? "dark" : "light"}`;
			vuetifyTheme.global.name.value = newThemeName;
		});
	};

	// Observar mudanças no tema
	watch(
		currentThemeName,
		(newThemeName) => {
			const theme = getThemeByName(newThemeName);
			if (theme) {
				applyTheme(newThemeName);
				localStorage.setItem("orixaTheme", newThemeName);
			}
		},
		{ immediate: true }
	);

	// Inicializar tema
	onMounted(() => {
		const savedTheme = localStorage.getItem("orixaTheme");
		if (savedTheme && getThemeByName(savedTheme)) {
			currentThemeName.value = savedTheme;
		}

		applyTheme(currentThemeName.value);
	});

	// Mudar tema
	const changeTheme = (themeName: string) => {
		const theme = getThemeByName(themeName);
		if (theme) {
			currentThemeName.value = themeName;
		}
	};

	// Alternar entre claro/escuro
	const toggleDark = () => {
		const currentName = vuetifyTheme.global.name.value;
		const themeName = currentName.replace(/-light|-dark/, "");
		const currentIsDark = currentName.includes("-dark");
		const newThemeName = `${themeName}-${currentIsDark ? "light" : "dark"}`;
		vuetifyTheme.global.name.value = newThemeName;
		localStorage.setItem("isDark", String(!currentIsDark));
	};

	// Verificar se é tema escuro
	const isDark = computed(() =>
		vuetifyTheme.global.name.value.includes("-dark")
	);

	return {
		currentThemeName: computed(() => currentThemeName.value),
		currentTheme,
		themes,
		changeTheme,
		toggleDark,
		isDark,
		applyTheme,
	};
}
